import type { OrderSummary } from '@/infrastructure/OMSClient';
import { formatBulkResult } from '@/pages/fulfillment/components/bulkResult';
import type { FulfillmentToastState } from '@/pages/fulfillment/components/useFulfillmentToast';
import type {
  FulfillmentOrderLine,
  FulfillmentTracking,
  PackingInvoice,
} from '@/infrastructure/FulfillmentAdminClient';
import {
  asRecord,
  asText,
  currentOutboundShipmentOf,
  isPendingParcel,
  pendingParcelsOf,
  deleoLabelNumberOf,
  deleoLabelRefusalOf,
  deleoTrackingOf,
  toDeleoLabelOrder,
} from '@/utils/fulfillmentDeleoLabel';

/** Deleo-family stickers have no carrier archive, so Admin draws them; other labels require a booked waybill. */
export interface CarrierLabel {
  orderId: number;
  trackingNumber: string;
  url: string;
}

/** An order the batch could not put a label against, and the reason the picker needs to hear. */
export interface MissingCarrierLabel {
  orderId: number;
  reason: string;
}

/** A parcel whose waybill we draw, and the order sheet the Deleo templates read to draw it. */
export interface DrawnCarrierLabel {
  orderId: number;
  trackingNumber: string;
  order: OrderSummary;
  /** The PACKING sheet printed behind the sticker, read when the batch is collected; absent when that read failed, and the sticker then prints alone. */
  invoice?: PackingInvoice;
}

export interface CarrierLabelBatch {
  labels: CarrierLabel[];
  drawn: DrawnCarrierLabel[];
  missing: MissingCarrierLabel[];
  failed?: MissingCarrierLabel[];
}

export const NOT_OUTBOUNDED = 'No waybill booked yet — Create Carrier Order on Order Detail';

/** Said when no selected order has a sheet to print, so the run does not read as if one were waiting. */
export const NOTHING_TO_PRINT = 'Nothing to print — none of these orders has a waybill booked yet.';

/** How many of the selection are left out for want of a waybill, which is what the sheet must say first. */
export const noWaybillCount = (batch: CarrierLabelBatch): number =>
  batch.missing.filter(({ reason }) => reason === NOT_OUTBOUNDED).length;

/** Only a selection held up by missing waybills alone gets the no-waybill tail; any other reason is read in the sheet. */
const nothingToPrint = (batch: CarrierLabelBatch): string =>
  noWaybillCount(batch) === batch.missing.length ? NOTHING_TO_PRINT : 'Nothing to print.';

/** Orders the batch has something to print for, whoever draws the sheet. */
export const printableCount = (batch: CarrierLabelBatch): number => batch.labels.length + batch.drawn.length;

/**
 * Whether the batch is worth a sheet: one that would only list "no waybill yet" rows read as a print
 * waiting to happen, so that case is told in the notification alone [SSD-6939 19-4]. Any other reason
 * still opens the sheet, where each row says what the archive or the request answered.
 */
export const opensLabelSheet = (batch: CarrierLabelBatch): boolean =>
  printableCount(batch) > 0 || batch.missing.length + (batch.failed ?? []).length > noWaybillCount(batch);

export const noWaybillNotice = (count: number): string =>
  `${count} selected ${count === 1 ? 'order has' : 'orders have'} no waybill yet and will not be printed.`;

/**
 * What the run found before printing. The sheet prints the processed orders straight after, so the line says so
 * rather than "not printed yet" [10/14 kiosk run]. Every per-order reason also remains in the details.
 */
export const labelRunOutcome = (label: string, batch: CarrierLabelBatch): FulfillmentToastState => {
  const failed = batch.failed ?? [];
  const printable = printableCount(batch);
  return formatBulkResult(
    label,
    printable,
    batch.missing.map(({ orderId, reason }) => ({ item: `Order ${orderId}`, reason })),
    failed.map(({ orderId, reason }) => ({ item: `Order ${orderId}`, reason })),
    printable > 0 ? 'Printing now.' : failed.length > 0 ? undefined : nothingToPrint(batch),
  );
};

export const NO_CARRIER_LABEL = 'The carrier has not returned a label for this parcel';

/** The parcel this order shipped on, or null while it has not shipped at all. */
export const trackingNumberOf = (payload: Record<string, unknown>, orderId: number): string | null => {
  const tracking = payload['tracking'];
  if (!Array.isArray(tracking)) {
    return null;
  }
  const parcel = currentOutboundShipmentOf(tracking as FulfillmentTracking[], orderId, pendingParcelsOf(payload));
  return parcel && !isPendingParcel(parcel) ? parcel.number?.trim() || null : null;
};

const rowsOf = (payload: Record<string, unknown>, key: string): Array<Record<string, unknown>> => {
  const rows = payload[key];
  return Array.isArray(rows) ? (rows as Array<Record<string, unknown>>) : [];
};

/**
 * The print payload's lines as the Deleo sheets read them. Those sheets want the name and the count
 * the picker sees on the shelf, and this queue's payload carries both inside the ingestion snapshot.
 */
const labelLinesOf = (payload: Record<string, unknown>): FulfillmentOrderLine[] =>
  rowsOf(payload, 'lines').map((line) => {
    const snapshot = asRecord(line['snapshot']) ?? {};
    return {
      lineNo: Number(line['lineNo'] ?? 0),
      sku: String(line['sku'] ?? ''),
      qtyOrdered: Number(line['qtyOrdered'] ?? 0),
      totalQuantity: Number(line['totalQuantity'] ?? 0),
      lineStatus: String(line['lineStatus'] ?? ''),
      isSample: Boolean(line['isSample']),
      productName: asText(snapshot['name']) ?? null,
      productNameKr: asText(snapshot['nameKor']) ?? null,
    };
  });

/** An unavailable Deleonex reference must never fall through to the courier's archived label. */
export const drawnLabelRefusalOf = (orderId: number, payload: Record<string, unknown>): string =>
  deleoLabelRefusalOf(
    deleoTrackingOf(
      rowsOf(payload, 'tracking') as unknown as FulfillmentTracking[],
      orderId,
      pendingParcelsOf(payload),
    ),
  );

/** The sticker this desk draws for either Deleo platform, or null when no sticker number is ready. */
export const drawnLabelOf = (orderId: number, payload: Record<string, unknown>): DrawnCarrierLabel | null => {
  const tracking = rowsOf(payload, 'tracking') as unknown as FulfillmentTracking[];
  const pendingParcels = pendingParcelsOf(payload);
  const parcel = deleoTrackingOf(tracking, orderId, pendingParcels);
  const stickerNumber = deleoLabelNumberOf(parcel).trim();
  if (deleoLabelRefusalOf(parcel) || !stickerNumber) {
    return null;
  }

  const header = asRecord(payload['header']) ?? {};
  return {
    orderId,
    trackingNumber: stickerNumber,
    order: toDeleoLabelOrder({
      orderId,
      shipping: header['shipping'],
      lines: labelLinesOf(payload),
      tracking,
      pendingParcels,
      isMarketing: Boolean(header['isMarketing']),
    }),
  };
};
